import FilterDrawer from 'components/Submissions/FilterDrawer';
import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/Submissions/PageProvider';
import Desktop from 'pages/Submissions/desktop';
import Mobile from 'pages/Submissions/mobile';
import Tablet from 'pages/Submissions/tablet';
import { useContext } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Icon, Loading, Stack, Text } from 'shell/components';

const PageLayout = () => {
    const { isLoading } = useApp();
    const history = useHistory();
    const { device } = useViewport();

    const {
        assignment,
        submissions,
        sortedFilteredSubmissions,
        exportSubmissions,
        resetSubmissions,
        filters,
        setFilters,
        setSubmissions,
        sortState,
        handleSetSortState,
    } = useContext(PageContext);

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.status.length) count++;
        return count;
    };

    const getSubmissionStatus = (submission) => {
        if (submission.grading) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'checkmark'} />
                    <Text>Reviewed</Text>
                </Stack>
            );
        }

        if (submission.ai) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'clock'} />
                    <Text>Ready for review</Text>
                </Stack>
            );
        }

        if (submission.is_failed) {
            return (
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Icon kind={'error'} />
                    <Text>Failed to process</Text>
                </Stack>
            );
        }

        return null;
    };

    const getSubmissionFinalGrade = (submission) => {
        if (!submission.grading || !assignment.ai_grading_enabled) return '--';
        const score = Object.entries(submission.grading.criteria).reduce((acc, [criterionId, optionId]) => {
            const { rate } = assignment.rubric.criteria
                .find(({ id }) => id === criterionId)
                .options.find(({ id }) => id === optionId);
            return acc + Number(rate);
        }, 0);
        const maxScore = assignment.rubric.criteria.reduce((acc, criterion) => {
            return acc + Math.max(...criterion.options.map(({ rate }) => Number(rate)));
        }, 0);
        return `${score}/${maxScore}`;
    };

    const handleClickBack = () => {
        return history.push(`/assignments/${assignment.id}`);
    };

    const handleClickViewAll = () => {
        const targetSubmission = submissions.find((submission) => submission.grading);
        return history.push(`/assignments/${assignment.id}/submissions/${targetSubmission.id}`);
    };

    const handleClickViewSubmission = (submission) => {
        return history.push(`/assignments/${assignment.id}/submissions/${submission.id}`);
    };

    const handleSelect = ({ key }) => {
        if (key === 'csv') {
            exportSubmissions();
        }
        if (key === 'delete') {
            resetSubmissions();
        }
    };

    const props = {
        assignment,
        submissions,
        setSubmissions,
        sortedFilteredSubmissions,
        getSubmissionStatus,
        getSubmissionFinalGrade,
        onClickBack: handleClickBack,
        onClickViewAll: handleClickViewAll,
        onClickViewSubmission: handleClickViewSubmission,
        onSelect: handleSelect,
        filters,
        setFilters,
        getFilterCount,
        sortState,
        handleSetSortState,
    };

    const renderResponsiveLayout = (device) => {
        if (!submissions.length) return <Redirect to={`/assignments/${assignment.id}`} />;

        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading || !submissions ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FilterDrawer sortState={sortState} handleSetSortState={handleSetSortState} />
        </>
    );
};

export default PageLayout;
